import type { GridPointerResultMessage } from '../websocket'

export type GridPointerKind = 'down' | 'move' | 'up' | 'cancel'
export type GridPointerButton = 'none' | 'left' | 'middle' | 'right'

const MAX_WHEEL_LINES = 100
const MAX_REQUEST_ID = 0xffffffff

export interface GridPointerTransport {
  sendGridPointer(
    requestId: number,
    kind: GridPointerKind,
    col: number,
    row: number,
    button: GridPointerButton,
  ): void
  sendGridWheel(col: number, row: number, lines: number): void
}

export interface GridPointerGeometry {
  cols(): number
  rows(): number
}

// Local rectangular selection. Task 13 renders it; the pointer machine only
// decides when a drag belongs to the browser instead of to a Lua pane.
export interface GridPointerSelection {
  begin(col: number, row: number): void
  extend(col: number, row: number): void
  finish(col: number, row: number): void
  cancel(): void
}

export interface GridPointerOptions {
  readonly surface: HTMLElement
  readonly transport: GridPointerTransport
  readonly geometry: GridPointerGeometry
  readonly enabled?: () => boolean
  readonly selection?: GridPointerSelection | null
}

interface PointerCell {
  readonly col: number
  readonly row: number
}

interface BufferedTerminal extends PointerCell {
  readonly kind: 'up' | 'cancel'
}

type PointerPhase = 'idle' | 'pending' | 'captured' | 'selecting'

function buttonName(button: number): GridPointerButton | null {
  switch (button) {
    case 0: return 'left'
    case 1: return 'middle'
    case 2: return 'right'
    default: return null
  }
}

function clamp(value: number, maximum: number): number {
  if (!Number.isFinite(value)) return 0
  if (value < 0) return 0
  return value > maximum ? maximum : value
}

export class GridPointer {
  readonly #transport: GridPointerTransport
  readonly #geometry: GridPointerGeometry
  readonly #enabled: () => boolean
  #selection: GridPointerSelection | null
  #surface: HTMLElement | null = null
  #phase: PointerPhase = 'idle'
  #requestSequence = 0
  #pendingRequestId = 0
  #pointerId: number | null = null
  #button: GridPointerButton = 'none'
  #anchor: PointerCell = { col: 0, row: 0 }
  #latest: PointerCell = { col: 0, row: 0 }
  #bufferedMove: PointerCell | null = null
  #bufferedTerminal: BufferedTerminal | null = null
  #abandoned: (PointerCell & { requestId: number; button: GridPointerButton }) | null = null
  #wheelRemainder = 0

  readonly #down = (event: Event): void => this.#handleDown(event as PointerEvent)
  readonly #move = (event: Event): void => this.#handleMove(event as PointerEvent)
  readonly #up = (event: Event): void => this.#handleTerminal(event as PointerEvent, 'up')
  readonly #cancelEvent = (event: Event): void =>
    this.#handleTerminal(event as PointerEvent, 'cancel')
  readonly #wheel = (event: Event): void => this.#handleWheel(event as WheelEvent)
  readonly #blur = (): void => this.cancelInteraction()

  constructor(options: GridPointerOptions) {
    this.#transport = options.transport
    this.#geometry = options.geometry
    this.#enabled = options.enabled ?? (() => true)
    this.#selection = options.selection ?? null
    this.attach(options.surface)
  }

  attach(surface: HTMLElement): void {
    if (surface === this.#surface) return
    this.#detach()
    this.#surface = surface
    surface.addEventListener('pointerdown', this.#down)
    surface.addEventListener('pointermove', this.#move)
    surface.addEventListener('pointerup', this.#up)
    surface.addEventListener('pointercancel', this.#cancelEvent)
    surface.addEventListener('wheel', this.#wheel, { passive: false })
    window.addEventListener('blur', this.#blur)
  }

  setSelection(selection: GridPointerSelection | null): void {
    this.#selection = selection
  }

  // Blur, resize, reconnect: the browser abandons the interaction and tells the
  // server, because the server still believes its pane holds capture.
  cancelInteraction(): void {
    this.#endInteraction(true)
  }

  // grid_cancel_interaction: the server already dropped its own capture, so
  // echoing a cancel back would be a second, spurious interaction.
  cancelFromServer(): void {
    this.#endInteraction(false)
  }

  handlePointerResult(message: GridPointerResultMessage): void {
    // An interaction abandoned while its down was outstanding may still have
    // been consumed by a Lua pane, which is now holding capture for a drag the
    // browser has already given up on. Nothing else will tell the server.
    const abandoned = this.#abandoned
    if (abandoned !== null && message.request_id === abandoned.requestId) {
      this.#abandoned = null
      if (message.result === 'consumed') {
        this.#transport.sendGridPointer(
          0, 'cancel', abandoned.col, abandoned.row, abandoned.button,
        )
      }
      return
    }
    if (this.#phase !== 'pending' || this.#pendingRequestId === 0 ||
        message.request_id !== this.#pendingRequestId) {
      return
    }
    this.#pendingRequestId = 0
    if (message.result === 'consumed') {
      this.#phase = 'captured'
      this.#replayToServer()
      return
    }
    // Unconsumed and error both fall open locally, and only a left drag has a
    // local behavior to fall open to.
    if (this.#button === 'left' && this.#selection !== null) {
      this.#phase = 'selecting'
      this.#replayToSelection()
      return
    }
    this.#resetInteraction()
  }

  dispose(): void {
    this.#detach()
    this.#surface = null
  }

  #detach(): void {
    const surface = this.#surface
    if (surface === null) return
    this.#releaseCapture()
    surface.removeEventListener('pointerdown', this.#down)
    surface.removeEventListener('pointermove', this.#move)
    surface.removeEventListener('pointerup', this.#up)
    surface.removeEventListener('pointercancel', this.#cancelEvent)
    surface.removeEventListener('wheel', this.#wheel)
    window.removeEventListener('blur', this.#blur)
    this.#resetInteraction()
    this.#wheelRemainder = 0
  }

  #handleDown(event: PointerEvent): void {
    if (!this.#enabled() || this.#phase !== 'idle') return
    const button = buttonName(event.button)
    if (button === null) return
    const cell = this.#cellFor(event)
    event.preventDefault()
    this.#pointerId = event.pointerId
    this.#button = button
    this.#anchor = cell
    this.#latest = cell
    this.#bufferedMove = null
    this.#bufferedTerminal = null
    this.#phase = 'pending'
    this.#pendingRequestId = this.#nextRequestId()
    this.#capture(event.pointerId)
    this.#transport.sendGridPointer(
      this.#pendingRequestId, 'down', cell.col, cell.row, button,
    )
  }

  #handleMove(event: PointerEvent): void {
    if (this.#phase === 'idle' || !this.#ownsPointer(event)) return
    const cell = this.#cellFor(event)
    this.#latest = cell
    if (this.#phase === 'pending') {
      // Only the newest position matters once the answer arrives.
      this.#bufferedMove = cell
      return
    }
    event.preventDefault()
    if (this.#phase === 'selecting') {
      this.#selection?.extend(cell.col, cell.row)
      return
    }
    this.#transport.sendGridPointer(0, 'move', cell.col, cell.row, this.#button)
  }

  #handleTerminal(event: PointerEvent, kind: 'up' | 'cancel'): void {
    if (this.#phase === 'idle' || !this.#ownsPointer(event)) return
    const cell = this.#cellFor(event)
    this.#latest = cell
    if (this.#phase === 'pending') {
      // One terminal event is retained; a second cannot change the outcome.
      if (this.#bufferedTerminal === null) {
        this.#bufferedTerminal = { kind, col: cell.col, row: cell.row }
      }
      return
    }
    event.preventDefault()
    if (this.#phase === 'selecting') {
      if (kind === 'up') this.#selection?.finish(cell.col, cell.row)
      else this.#selection?.cancel()
    } else {
      this.#transport.sendGridPointer(0, kind, cell.col, cell.row, this.#button)
    }
    this.#resetInteraction()
  }

  #handleWheel(event: WheelEvent): void {
    if (!this.#enabled()) return
    const lineHeight = this.#lineHeight()
    const delta = event.deltaMode === 1
        ? event.deltaY
        : event.deltaMode === 2
            ? event.deltaY * Math.max(1, this.#geometry.rows())
            : event.deltaY / lineHeight
    if (!Number.isFinite(delta)) return
    event.preventDefault()
    // A partial line accumulated scrolling one way must not eat the first
    // notch of a scroll back the other way.
    if (delta < 0 !== this.#wheelRemainder < 0) this.#wheelRemainder = 0
    this.#wheelRemainder += delta
    // Fractional trackpad deltas accumulate rather than being dropped; only
    // whole lines are ever put on the wire.
    const lines = Math.trunc(this.#wheelRemainder)
    if (lines === 0) return
    this.#wheelRemainder -= lines
    const cell = this.#cellFor(event)
    const bounded = Math.max(-MAX_WHEEL_LINES, Math.min(MAX_WHEEL_LINES, lines))
    this.#transport.sendGridWheel(cell.col, cell.row, bounded)
  }

  #replayToServer(): void {
    const move = this.#bufferedMove
    const terminal = this.#bufferedTerminal
    this.#bufferedMove = null
    this.#bufferedTerminal = null
    if (move !== null) {
      this.#transport.sendGridPointer(0, 'move', move.col, move.row, this.#button)
    }
    if (terminal === null) return
    this.#transport.sendGridPointer(
      0, terminal.kind, terminal.col, terminal.row, this.#button,
    )
    this.#resetInteraction()
  }

  #replayToSelection(): void {
    const move = this.#bufferedMove
    const terminal = this.#bufferedTerminal
    this.#bufferedMove = null
    this.#bufferedTerminal = null
    this.#selection?.begin(this.#anchor.col, this.#anchor.row)
    if (move !== null) this.#selection?.extend(move.col, move.row)
    if (terminal === null) return
    if (terminal.kind === 'up') this.#selection?.finish(terminal.col, terminal.row)
    else this.#selection?.cancel()
    this.#resetInteraction()
  }

  #endInteraction(notifyServer: boolean): void {
    if (this.#phase === 'idle') return
    if (this.#phase === 'captured' && notifyServer) {
      this.#transport.sendGridPointer(
        0, 'cancel', this.#latest.col, this.#latest.row, this.#button,
      )
    }
    if (this.#phase === 'selecting') this.#selection?.cancel()
    // A pending request is abandoned rather than forgotten: its late answer can
    // never resurrect the interaction, but it still has to be answered if the
    // server granted capture for it. A server-side cancellation already covers
    // that case, so only notify when we are the ones abandoning.
    if (this.#phase === 'pending' && this.#pendingRequestId !== 0 && notifyServer) {
      this.#abandoned = {
        requestId: this.#pendingRequestId,
        button: this.#button,
        col: this.#latest.col,
        row: this.#latest.row,
      }
    }
    this.#resetInteraction()
  }

  #resetInteraction(): void {
    this.#releaseCapture()
    this.#phase = 'idle'
    this.#pendingRequestId = 0
    this.#pointerId = null
    this.#button = 'none'
    this.#bufferedMove = null
    this.#bufferedTerminal = null
  }

  #ownsPointer(event: PointerEvent): boolean {
    return this.#pointerId === null || event.pointerId === this.#pointerId
  }

  #nextRequestId(): number {
    this.#requestSequence = this.#requestSequence >= MAX_REQUEST_ID
        ? 1 : this.#requestSequence + 1
    return this.#requestSequence
  }

  #capture(pointerId: number): void {
    const surface = this.#surface as (HTMLElement & {
      setPointerCapture?: (id: number) => void
    }) | null
    try {
      surface?.setPointerCapture?.(pointerId)
    } catch {
      // Capture is provisional; losing it only costs events outside the canvas.
    }
  }

  #releaseCapture(): void {
    const pointerId = this.#pointerId
    if (pointerId === null) return
    const surface = this.#surface as (HTMLElement & {
      releasePointerCapture?: (id: number) => void
      hasPointerCapture?: (id: number) => boolean
    }) | null
    try {
      if (surface?.hasPointerCapture?.(pointerId) !== false) {
        surface?.releasePointerCapture?.(pointerId)
      }
    } catch {
      // The browser drops capture on its own when the surface goes away.
    }
  }

  #lineHeight(): number {
    const rect = this.#surface?.getBoundingClientRect()
    const rows = Math.max(1, this.#geometry.rows())
    const height = rect && rect.height > 0 ? rect.height : rows
    return height / rows
  }

  #cellFor(event: { clientX: number; clientY: number }): PointerCell {
    const rect = this.#surface?.getBoundingClientRect()
    const cols = Math.max(1, this.#geometry.cols())
    const rows = Math.max(1, this.#geometry.rows())
    if (!rect || rect.width <= 0 || rect.height <= 0) return { col: 0, row: 0 }
    const col = Math.floor((event.clientX - rect.left) / (rect.width / cols))
    const row = Math.floor((event.clientY - rect.top) / (rect.height / rows))
    return { col: clamp(col, cols - 1), row: clamp(row, rows - 1) }
  }
}
